// ДРУГ.2: модалка «Выбери юзернейм».
//
// Показывается на любом экране, если /api/profile/me вернул needsNickSetup=true.
// До закрытия — игрок не виден в поиске чумб (см. routes/friends.ts:players/search
// фильтрует needs_nick_setup=false). После сохранения флаг снимается.
//
// Закрыть кнопкой ✕ можно, но банер «нужно выбрать юзернейм» останется
// (открывается заново при следующем заходе на главную). Для MVP делаем
// блокирующую модалку без ✕ — иначе игрок может её игнорировать вечно.

import { useEffect, useState } from 'react';
import { getProfile, updateProfile } from '../api/client';
import { useTelegram } from '../telegram/useTelegram';
import { useToast } from './Toast';
import { useTr } from '../i18n/useTr';

const NICK_REGEX = /^[a-zA-Z0-9_]{3,20}$/;

export function NickSetupModal() {
  const [open, setOpen] = useState(false);
  const [nick, setNick] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { hapticTap, hapticSuccess, hapticError } = useTelegram();
  const toast = useToast(s => s.show);
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';

  // Проверяем при mount-е — нужна ли модалка.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getProfile();
        if (!cancelled && p.player.needsNickSetup) setOpen(true);
      } catch { /* offline / не залогинен — не показываем */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!open) return null;

  const validate = (s: string): string | null => {
    if (s.length < 3) return tr('Минимум 3 символа', 'Min 3 characters');
    if (s.length > 20) return tr('Максимум 20 символов', 'Max 20 characters');
    if (!NICK_REGEX.test(s)) return tr('Только латинские буквы, цифры и подчёркивание', 'Latin letters, digits and underscore only');
    return null;
  };

  const onSave = async () => {
    const cleaned = nick.trim();
    const v = validate(cleaned);
    if (v) { setError(v); hapticError(); return; }
    hapticTap();
    setBusy(true);
    setError(null);
    try {
      await updateProfile({ nick: cleaned });
      hapticSuccess();
      toast(tr('Юзернейм сохранён', 'Username saved'), 'green');
      setOpen(false);
    } catch (e: any) {
      hapticError();
      const code = e?.payload?.error;
      if (code === 'nick_taken') setError(tr('Этот юзернейм уже занят, попробуй другой', 'Username taken, try another'));
      else setError(e?.payload?.message || tr('Не получилось сохранить', 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.92)',
        zIndex: 350,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16
      }}
    >
      <div
        style={{
          background: 'var(--bg-2, #101018)',
          width: '100%', maxWidth: 380,
          boxShadow: 'inset 0 0 0 0.5px var(--neon-cy, #3DF0FF)',
          padding: 20,
          display: 'flex', flexDirection: 'column', gap: 14
        }}
      >
        <div style={{
          color: 'var(--neon-cy, #3DF0FF)',
          fontSize: 13, letterSpacing: '0.14em',
          fontWeight: 700, textAlign: 'center'
        }}>
          ► {tr('ВЫБЕРИ ЮЗЕРНЕЙМ', 'CHOOSE USERNAME')}
        </div>
        <div style={{ color: 'var(--t-md)', fontSize: 13, lineHeight: 1.5 }}>
          {tr(
            'Чумба, чтобы тебя могли найти в поиске и добавить в друзья — выбери себе уникальный юзернейм.',
            'Choom, pick a unique username so others can find and add you in search.'
          )}
          <br /><br />
          <span style={{ color: 'var(--t-lo)', fontSize: 11 }}>
            {tr(
              'Только латинские буквы, цифры и подчёркивание. От 3 до 20 символов.',
              'Latin letters, digits and underscore only. From 3 to 20 characters.'
            )}
          </span>
        </div>
        <input
          type="text"
          value={nick}
          onChange={(e) => { setNick(e.target.value); setError(null); }}
          placeholder={isEn ? 'e.g. cyber_ivan' : 'напр. cyber_ivan'}
          maxLength={20}
          disabled={busy}
          autoFocus
          style={{
            background: 'var(--bg-3, #0a0a14)',
            color: 'var(--t-hi)',
            border: `0.5px solid ${error ? '#ff7a7a' : 'var(--line, #555)'}`,
            padding: '10px 12px',
            fontSize: 14,
            fontFamily: 'inherit',
            letterSpacing: 0.3,
            outline: 'none'
          }}
        />
        {error && (
          <div style={{ color: '#ff7a7a', fontSize: 11 }}>{error}</div>
        )}
        <button
          onClick={onSave}
          disabled={busy || !nick.trim()}
          className="cd"
          style={{
            background: 'var(--neon-cy, #3DF0FF)',
            color: '#06131A',
            border: 'none',
            padding: '12px',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            cursor: busy || !nick.trim() ? 'default' : 'pointer',
            opacity: busy || !nick.trim() ? 0.5 : 1
          }}
        >
          {busy ? tr('Сохранение…', 'Saving…') : tr('Сохранить', 'Save')}
        </button>
      </div>
    </div>
  );
}
